import { IDimPrimitive } from '../plan-primitive';
import {
    DXF_DIM_OVERRUN_MM,
    DXF_DIM_TEXT_GAP_MM,
    DXF_DIM_TEXT_MM,
    IDimAxes,
    axesOf,
    extensionLine,
    line,
    measuredPoint,
    pointOn,
    slash,
} from './dxf-dims';
import {
    EDxfEntityKind,
    EDxfHorizontalAlign,
    EDxfVerticalAlign,
    IDxfBounds,
    IDxfEntity,
    IDxfLine,
    IDxfPoint,
    IDxfText,
    boundsOfPoints,
    growBounds,
    textCorners,
    textWidth,
} from './dxf-entities';
import { IDxfPaneFit, toPaneMm } from './dxf-fit';
import { EDxfLayerKind, paneLayerName } from './dxf-layers';
import { IDxfSegment, IDxfSketch, boxesMeet, segmentHitsBox } from './dxf-sketch';
import { toDxfText } from './dxf-text';

/*
 * Dimensions of a detail view laid out by ГОСТ 2.307 on paper: every dimension line stands
 * outside the part it measures, the first row 10 mm from the part's contour and each next row
 * 7 mm farther out. Shorter dimensions take the inner rows, so an extension line never crosses a
 * shorter dimension's number. A number too long for its span goes past one end of it, beside the
 * extension line, on the dimension line carried on under it. All sizes are paper millimetres
 * times the view's denominator.
 */

/** ГОСТ 2.307: the first dimension line at least 10 mm from the contour on paper. */
export const DXF_DIM_FIRST_ROW_MM: number = 10;
/** ГОСТ 2.307: parallel dimension lines at least 7 mm apart on paper. */
export const DXF_DIM_ROW_STEP_MM: number = 7;
/** Rows tried before a dimension takes the outermost one regardless. */
const MAX_ROWS: number = 8;
/** Air kept around a number on paper, millimetres. */
const LABEL_AIR_MM: number = 0.5;
/** Gap between an end of the span and a number moved past it, on paper. */
const OUTSIDE_GAP_MM: number = 1.5;
/** Half the thickness a dimension line occupies, on paper. */
const LINE_HALF_MM: number = 0.1;
/** A turn this small against the two segments' lengths is no turn: a point on the line, not beside it. */
const CROSS_TOLERANCE: number = 1e-9;

/** Where a number stands along its dimension line. */
enum ELabelPlace {
    Inside = 'inside',
    AfterHigh = 'after-high',
    BeforeLow = 'before-low',
}

/** A dimension ready to be placed: its span, the points it measures and the side it goes to. */
export interface IDimTask {
    prim: IDimPrimitive;
    axes: IDimAxes;
    low: number;
    high: number;
    lowFeature: IDxfPoint;
    highFeature: IDxfPoint;
    /** The index of the part of the drawing the dimension measures. */
    part: number;
    /** +1 — beyond the part's max edge across the dimension, −1 — beyond its min edge. */
    side: 1 | -1;
    /** +1 when the span lies in the part's upper half along the dimension: a number that does not fit goes that way. */
    outward: 1 | -1;
    /** The part's contour edge on that side, across the dimension. */
    edge: number;
}

/** A placed dimension: its entities and what it takes up on the drawing. */
interface IPlacedDim {
    entities: (IDxfLine | IDxfText)[];
    lineBox: IDxfBounds;
    textBox: IDxfBounds;
    extensions: IDxfSegment[];
}

/** What is already taken around a part: number boxes, dimension lines and extension lines. */
interface IDimTaken {
    texts: IDxfBounds[];
    lines: IDxfBounds[];
    extensions: IDxfSegment[];
}

function labelWidth(task: IDimTask, scale: number): number {
    return textWidth(toDxfText(task.prim.label), DXF_DIM_TEXT_MM * scale);
}

/** How far a point lies from a box; zero inside it. */
export function distanceToBox(point: IDxfPoint, box: IDxfBounds): number {
    const dx: number = Math.max(box.minX - point[0], 0, point[0] - box.maxX);
    const dy: number = Math.max(box.minY - point[1], 0, point[1] - box.maxY);
    return Math.hypot(dx, dy);
}

/** The index of the box nearest to a point. */
export function nearestBox(point: IDxfPoint, boxes: readonly IDxfBounds[]): number {
    let best: number = 0;
    boxes.forEach((box: IDxfBounds, index: number): void => {
        if (distanceToBox(point, box) < distanceToBox(point, boxes[best])) {
            best = index;
        }
    });
    return best;
}

/**
 * The side of the part nearer to the measured points, so the extension lines stay short; the
 * side the screen chose when both are as near.
 */
function sideOf(features: [number, number], [partLow, partHigh]: [number, number], screen: 1 | -1): 1 | -1 {
    const toHigh: number = partHigh - Math.max(...features);
    const toLow: number = Math.min(...features) - partLow;
    if (Math.abs(toHigh - toLow) < 1e-6) {
        return screen;
    }
    return toHigh < toLow ? 1 : -1;
}

/** Twice the signed area of the triangle start → end → point: its sign tells which side of the line the point is on. */
function turn(start: IDxfPoint, end: IDxfPoint, point: IDxfPoint): number {
    return (end[0] - start[0]) * (point[1] - start[1]) - (end[1] - start[1]) * (point[0] - start[0]);
}

/** Whether two segments cross at a point inside both; touching at an end or running along does not count. */
function segmentsCross([a, b]: IDxfSegment, [c, d]: IDxfSegment): boolean {
    const tolerance: number = CROSS_TOLERANCE * Math.hypot(b[0] - a[0], b[1] - a[1]) * Math.hypot(d[0] - c[0], d[1] - c[1]);
    const sign: (value: number) => number = (value: number): number => (Math.abs(value) <= tolerance ? 0 : Math.sign(value));
    const [abc, abd, cda, cdb]: number[] = [turn(a, b, c), turn(a, b, d), turn(c, d, a), turn(c, d, b)].map(sign);
    return abc * abd < 0 && cda * cdb < 0;
}

/** How many contour lines the extension lines from the measured points to a part edge would cross. */
function crossingsTo(features: readonly IDxfPoint[], axes: IDimAxes, edge: number, segments: readonly IDxfSegment[]): number {
    return features.reduce((sum: number, feature: IDxfPoint): number => {
        const extension: IDxfSegment = [feature, pointOn(axes, feature[axes.along], edge)];
        return sum + segments.filter((segment: IDxfSegment): boolean => segmentsCross(extension, segment)).length;
    }, 0);
}

/**
 * The side a dimension goes to. ГОСТ 2.307 keeps extension lines off the contour: the side whose
 * extension lines cross fewer contour lines wins; between two as clean, the nearer one.
 */
function chooseSide(features: [IDxfPoint, IDxfPoint], axes: IDimAxes, part: [number, number], sketch: IDxfSketch, screen: 1 | -1): 1 | -1 {
    const [partLow, partHigh]: [number, number] = part;
    // An axis or a walking line runs across the part by design: only the outline counts.
    const outline: IDxfSegment[] = sketch.contour ?? sketch.segments;
    const toHigh: number = crossingsTo(features, axes, partHigh, outline);
    const toLow: number = crossingsTo(features, axes, partLow, outline);
    if (toHigh !== toLow) {
        return toHigh < toLow ? 1 : -1;
    }
    return sideOf([features[0][axes.across], features[1][axes.across]], part, screen);
}

/**
 * The task of one dimension: its span in the view's millimetres, the drawing points it measures
 * (its own ends where the drawing has none), the part those points belong to and the side of that
 * part its extension lines reach without crossing the contour.
 */
export function dimTask(prim: IDimPrimitive, fit: IDxfPaneFit, sketch: IDxfSketch, parts: readonly IDxfBounds[]): IDimTask {
    const axes: IDimAxes = axesOf(prim);
    const from: IDxfPoint = toPaneMm(fit, prim.from);
    const to: IDxfPoint = toPaneMm(fit, prim.to);
    const across: number = from[axes.across];
    const low: number = Math.min(from[axes.along], to[axes.along]);
    const high: number = Math.max(from[axes.along], to[axes.along]);
    const lowEnd: IDxfPoint = pointOn(axes, low, across);
    const highEnd: IDxfPoint = pointOn(axes, high, across);
    const lowFeature: IDxfPoint = measuredPoint(lowEnd, axes, sketch) ?? lowEnd;
    const highFeature: IDxfPoint = measuredPoint(highEnd, axes, sketch) ?? highEnd;
    const partIndex: number = nearestBox(lowFeature, parts);
    const part: IDxfBounds = parts[partIndex];
    const [partLow, partHigh]: [number, number] = axes.across === 0 ? [part.minX, part.maxX] : [part.minY, part.maxY];
    // Twice the middle of the part along the dimension: compared with low + high.
    const partSpanAlong: number = axes.along === 0 ? part.minX + part.maxX : part.minY + part.maxY;
    const side: 1 | -1 = chooseSide(
        [lowFeature, highFeature],
        axes,
        [partLow, partHigh],
        sketch,
        across >= (partLow + partHigh) / 2 ? 1 : -1
    );
    return {
        prim,
        axes,
        low,
        high,
        side,
        lowFeature,
        highFeature,
        part: partIndex,
        edge: side > 0 ? partHigh : partLow,
        outward: low + high >= partSpanAlong ? 1 : -1,
    };
}

/** Where the number's middle stands along the line for a place. */
function labelCenter(task: IDimTask, place: ELabelPlace, width: number, scale: number): number {
    switch (place) {
        case ELabelPlace.Inside:
            return (task.low + task.high) / 2;
        case ELabelPlace.AfterHigh:
            return task.high + OUTSIDE_GAP_MM * scale + width / 2;
        case ELabelPlace.BeforeLow:
            return task.low - OUTSIDE_GAP_MM * scale - width / 2;
    }
}

/** The number on its line: above a horizontal line, left of a vertical one and read from below. */
function labelOf(task: IDimTask, fit: IDxfPaneFit, lineAt: number, center: number): IDxfText {
    const scale: number = fit.scale;
    const gap: number = DXF_DIM_TEXT_GAP_MM * scale;
    const horizontal: boolean = task.axes.along === 0;
    return {
        kind: EDxfEntityKind.Text,
        layer: paneLayerName(fit.paneId, EDxfLayerKind.Dim),
        ltScale: scale,
        at: horizontal ? [center, lineAt + gap] : [lineAt - gap, center],
        text: toDxfText(task.prim.label),
        rotationDeg: horizontal ? 0 : 90,
        hAlign: EDxfHorizontalAlign.Center,
        vAlign: EDxfVerticalAlign.Bottom,
        widthFactor: 1,
        height: DXF_DIM_TEXT_MM * scale,
    };
}

/** The dimension at one row with its number at one place. */
function buildDim(task: IDimTask, fit: IDxfPaneFit, row: number, place: ELabelPlace): IPlacedDim {
    const scale: number = fit.scale;
    const layer: string = paneLayerName(fit.paneId, EDxfLayerKind.Dim);
    const axes: IDimAxes = task.axes;
    const lineAt: number = task.edge + task.side * (DXF_DIM_FIRST_ROW_MM + row * DXF_DIM_ROW_STEP_MM) * scale;
    const width: number = labelWidth(task, scale);
    const center: number = labelCenter(task, place, width, scale);
    const label: IDxfText = labelOf(task, fit, lineAt, center);
    const overrun: number = DXF_DIM_OVERRUN_MM * scale;
    // The line is carried on under a number moved past an end.
    const start: number = Math.min(task.low - overrun, center - width / 2);
    const end: number = Math.max(task.high + overrun, center + width / 2);
    const low: IDxfPoint = pointOn(axes, task.low, lineAt);
    const high: IDxfPoint = pointOn(axes, task.high, lineAt);
    const extensions: IDxfLine[] = [
        ...extensionLine(low, task.lowFeature, axes, layer, scale),
        ...extensionLine(high, task.highFeature, axes, layer, scale),
    ];
    const half: number = LINE_HALF_MM * scale;
    // Only the measured span counts against other lines: a chain shares its ends, and a line
    // carried on under a number runs on in the neighbour's line without hiding it.
    const taken: IDxfBounds = boundsOfPoints([pointOn(axes, task.low, lineAt - half), pointOn(axes, task.high, lineAt + half)]);
    return {
        entities: [
            line(layer, scale, pointOn(axes, start, lineAt), pointOn(axes, end, lineAt)),
            ...extensions,
            slash(low, layer, scale),
            slash(high, layer, scale),
            label,
        ],
        lineBox: taken,
        textBox: growBounds(boundsOfPoints(textCorners(label)), LABEL_AIR_MM * scale),
        extensions: extensions.map((extension: IDxfLine): IDxfSegment => [extension.from, extension.to]),
    };
}

/** Whether a placed dimension runs into anything already taken. */
function clashes(dim: IPlacedDim, taken: IDimTaken): boolean {
    const hitsBox: (box: IDxfBounds) => boolean = (box: IDxfBounds): boolean => boxesMeet(box, dim.textBox) || boxesMeet(box, dim.lineBox);
    return (
        taken.texts.some(hitsBox) ||
        taken.lines.some(hitsBox) ||
        taken.extensions.some((segment: IDxfSegment): boolean => segmentHitsBox(segment, dim.textBox))
    );
}

/** Whether a number stands across a place where a longer dimension's extension line will run. */
function crossesAhead(dim: IPlacedDim, task: IDimTask, ahead: readonly number[], scale: number): boolean {
    // The glyphs themselves, without the air kept around them: a line may pass through the air.
    const air: number = LABEL_AIR_MM * scale;
    const [from, to]: [number, number] =
        task.axes.along === 0 ? [dim.textBox.minX + air, dim.textBox.maxX - air] : [dim.textBox.minY + air, dim.textBox.maxY - air];
    return ahead.some((at: number): boolean => at > from && at < to);
}

/** The places a number may take: inside its span, or past the end facing out of the part first. */
function placesOf(task: IDimTask, scale: number): ELabelPlace[] {
    if (task.high - task.low >= labelWidth(task, scale) + 2 * OUTSIDE_GAP_MM * scale) {
        return [ELabelPlace.Inside];
    }
    return task.outward > 0 ? [ELabelPlace.AfterHigh, ELabelPlace.BeforeLow] : [ELabelPlace.BeforeLow, ELabelPlace.AfterHigh];
}

/**
 * One dimension in the innermost free row: the number inside its span when it fits there with
 * air for the slashes, otherwise past the end facing out of the part or, failing that, the other.
 */
function placeDim(task: IDimTask, fit: IDxfPaneFit, taken: IDimTaken, ahead: readonly number[]): IPlacedDim {
    const places: ELabelPlace[] = placesOf(task, fit.scale);
    // First keep the number off the extension lines still to come; if no row allows that, only off what is drawn.
    for (const strict of [true, false]) {
        for (let row: number = 0; row < MAX_ROWS; row++) {
            const free: IPlacedDim | undefined = places
                .map((place: ELabelPlace): IPlacedDim => buildDim(task, fit, row, place))
                .find(
                    (candidate: IPlacedDim): boolean =>
                        !clashes(candidate, taken) && !(strict && crossesAhead(candidate, task, ahead, fit.scale))
                );
            if (free) {
                return free;
            }
        }
    }
    return buildDim(task, fit, MAX_ROWS, places[0]);
}

/** Where the extension lines of the dimensions after `index` run: their ends on the same side of the part. */
function aheadOf(ordered: readonly IDimTask[], index: number): number[] {
    const task: IDimTask = ordered[index];
    return ordered
        .slice(index + 1)
        .filter((later: IDimTask): boolean => later.axes.along === task.axes.along && later.side === task.side)
        .flatMap((later: IDimTask): number[] => [later.low, later.high]);
}

/**
 * The dimensions of one part, shortest first, each in the innermost row free for it. `labels`
 * are the boxes of the part's own texts, which the numbers keep clear of.
 */
export function layoutPartDims(tasks: readonly IDimTask[], fit: IDxfPaneFit, labels: readonly IDxfBounds[]): IDxfEntity[] {
    const taken: IDimTaken = {
        texts: labels.map((label: IDxfBounds): IDxfBounds => growBounds(label, LABEL_AIR_MM * fit.scale)),
        lines: [],
        extensions: [],
    };
    const ordered: IDimTask[] = [...tasks].sort((a: IDimTask, b: IDimTask): number => a.high - a.low - (b.high - b.low) || a.low - b.low);
    return ordered.flatMap((task: IDimTask, index: number): IDxfEntity[] => {
        const dim: IPlacedDim = placeDim(task, fit, taken, aheadOf(ordered, index));
        taken.texts.push(dim.textBox);
        taken.lines.push(dim.lineBox);
        taken.extensions.push(...dim.extensions);
        return dim.entities;
    });
}
